import { useEffect } from 'react'
import { isRouteErrorResponse, useRouteError } from 'react-router'
import { isAxiosError } from 'axios'
import { Button } from '@/components/ui/button'
import { reportUnexpectedClientFailure } from '@/shared/lib/reportUnexpectedClientFailure'

function NetworkError() {
  const handleReload = () => {
    window.location.reload()
  }
  return (
    <div className='flex max-w-md flex-col items-center justify-center space-y-6 mx-auto text-center w-full h-svh bg-background'>
      <h1 className='text-2xl font-bold'>Connection Error</h1>
      <p className='text-muted-foreground'>
        We are unable to connect to the server. Please check your internet connection and try again.
      </p>
      <Button onClick={handleReload}>Reload</Button>
    </div>
  )
}

function isNotFoundError(error: unknown): boolean {
  if (isRouteErrorResponse(error)) {
    return error.status === 404
  }

  if (error instanceof Response) {
    return error.status === 404
  }

  return isAxiosError(error) && error.response?.status === 404
}

export function NotFoundPage() {
  return (
    <div className='flex max-w-md flex-col items-center justify-center space-y-6 mx-auto text-center w-full h-svh bg-background'>
      <h1 className='text-2xl font-bold'>404</h1>
      <p className='text-muted-foreground'>
        Sorry, the page you are looking for could not be found.
      </p>
    </div>
  )
}

export default function ErrorPage() {
  const error = useRouteError()
  const isNotFound = isNotFoundError(error)

  useEffect(() => {
    if (isNotFound) {
      return
    }

    reportUnexpectedClientFailure({
      error,
      severity: 'crash',
    })
  }, [error, isNotFound])

  if (import.meta.env.DEV) {
    // Intentional: log route errors for debugging when error boundary catches
    console.error(error)
  }

  if (isAxiosError(error) && error.code === 'ERR_NETWORK') {
    return <NetworkError />
  }

  if (isNotFound) {
    return <NotFoundPage />
  }

  return (
    <div className='flex max-w-md flex-col items-center justify-center space-y-6 mx-auto text-center w-full h-svh bg-background'>
      <h1 className='text-2xl font-bold'>Oops!</h1>
      <p className='text-muted-foreground'>Sorry, an unexpected error has occurred.</p>
    </div>
  )
}
